/* eslint-disable @typescript-eslint/no-explicit-any */
import { IRentersNegativation } from "@/types/renters-negativation.type";
import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";

const API_URL = `${import.meta.env.VITE_API_URL}/negativations`;

const makeRequestDebounced = createDebounce(makeRequest, 200);

type GetRentersNegativationOptions = {
  status?: string | string[];
  search?: string;
  startDate?: string;
  endDate?: string;
  startDateAtualizadoEm?: string;
  endDateAtualizadoEm?: string;
  page?: number;
  itemsPerPage?: string;
  orderBy?: string;
  technicalConsultant?: string;
  situation?: string;
  fields?: string;
};

export async function getRentersNegativation(
  options: GetRentersNegativationOptions = {}
) {
  const {
    status = [],
    search = "",
    startDate = "",
    endDate = "",
    startDateAtualizadoEm = "",
    endDateAtualizadoEm = "",
    page = 1,
    itemsPerPage = "",
    orderBy = "-createdAt",
    technicalConsultant = "",
    situation = "",
    fields = "renterName, renterId, companyId, value, invoiceNumber, status, createdAt, dunningNumber",
  } = options;
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });
  const statusParam =
    Array.isArray(status)
      ? status.length > 0
        ? status.join(",")
        : null
      : typeof status === "string" && status.trim() !== ""
        ? status.trim()
        : null;

  if (statusParam) {
    params.append("filters[status]", statusParam);
  }
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());
  startDateAtualizadoEm &&
    params.append(
      "filters[startDateAtualizadoEm]",
      startDateAtualizadoEm.toString()
    );
  endDateAtualizadoEm &&
    params.append(
      "filters[endDateAtualizadoEm]",
      endDateAtualizadoEm.toString()
    );
  technicalConsultant &&
    params.append("filters[technicalConsultant]", technicalConsultant);
  situation && params.append("filters[situation]", situation);

  const url = `${API_URL}/?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IRentersNegativation[]
  > & { totalItems: number };
  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
}

export async function getNegativationById(negativationId: string) {
  const url = `${API_URL}/${negativationId}`;
  const result = (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<IRentersNegativation>
  >;

  return result;
}

export async function updateNegatiation(
  negaticationId: string,
  negativationData: any
) {
  const url = `${API_URL}/${negaticationId}`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(negativationData),
  };
  const result = (await makeRequestDebounced(url, options)) as Promise<
    ApiResponseType<IRentersNegativation>
  >;

  return result;
}

export async function createNegativation(data: any) {
  const url = `${API_URL}/`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  const result = (await makeRequestDebounced(
    url,
    options
  )) as ApiResponseType<any>;

  return { ok: result.ok, data: result.data, message: result.message };
}

export async function updatePaymentNegativation(data: any) {
  const url = `${API_URL}/edit/payment`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  const result = (await makeRequestDebounced(
    url,
    options
  )) as ApiResponseType<any>;

  return { ok: result.ok, data: result.data, message: result.message };
}

export async function cancelNegativation(
  negativationId: string,
  reason: string
) {
  const url = `${API_URL}/${negativationId}`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  };
  const result = (await makeRequestDebounced(url, options)) as Promise<
    ApiResponseType<IRentersNegativation>
  >;

  return result;
}

export async function verifyPaymentsNegativation() {
  const url = `${API_URL}/verify/payments`;
  const options = {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  };
  const result = (await makeRequestDebounced(url, options)) as Promise<
    ApiResponseType<string[]>
  >;

  return result;
}
